/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Interface representing a date range.
 */
export interface ComparisonDateRange {
  startDate: string;
  endDate: string;
}

/**
 * Interface representing station comparison state.
 */
export interface StationComparisonInterface {
  selectedStationIds: string[];
  dateRange: ComparisonDateRange;
  isFetchingStationDetails: boolean;
  isFetchingLocationDetails?: boolean;
}

/**
 * Interface representing payload to update a single station ID by index.
 */
export interface UpdateStationPayload {
  index: number;
  id: string;
}

const currentDate = new Date().toISOString().split('T')[0];

const initialState: StationComparisonInterface = {
  selectedStationIds: [],
  dateRange: {
    startDate: currentDate,
    endDate: currentDate,
  },
  isFetchingStationDetails: false,
};

/**
 * Station comparison slice.
 */
export const stationComparisonSlice = createSlice({
  name: 'station-comparison',
  initialState,
  reducers: {
    /**
     * Setting selected station IDs.
     * @param {any} state - The current state.
     * @param {any} action - The action containing array of station IDs.
     */
    setSelectedStationIds: (state, action: PayloadAction<string[]>) => {
      state.selectedStationIds = action.payload;
    },
    /**
     * Setting comparison date range.
     * @param {any} state - The current state.
     * @param {any} action - The action containing date range.
     */
    setComparisonDateRange: (state, action: PayloadAction<ComparisonDateRange>) => {
      state.dateRange = action.payload;
    },
    /**
     * Setting station details fetching state.
     * @param {any} state - The current state.
     * @param {any} action - The action containing boolean fetching state.
     */
    setIsFetchingStationDetails: (state, action: PayloadAction<boolean>) => {
      state.isFetchingStationDetails = action.payload;
    },
    /**
     * Adding selected station ids
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    addSelectedStationIds: (state, action: PayloadAction<UpdateStationPayload>) => {
      const { id, index } = action.payload;

      // Expand array up to 4 if needed
      while (state.selectedStationIds.length < 4) {
        state.selectedStationIds.push('');
      }

      // Replace that index with the new id
      state.selectedStationIds[index] = id;
    },
  },
});

/**
 * Action creators for station comparison slice.
 */
export const {
  setSelectedStationIds,
  setComparisonDateRange,
  setIsFetchingStationDetails,
  addSelectedStationIds,
} = stationComparisonSlice.actions;

export default stationComparisonSlice.reducer;

export const setIsFetchingLocationDetails = setIsFetchingStationDetails;
